import React, { useEffect, useRef, useCallback } from 'react';
import { NewNGPageProps } from '../types';
import { TimeLinedVideo } from './TimeLinedVideo';
import { VideoInfoSection } from './VideoInfoSection';
import { TopNews } from './TopNews';
import { NewsGrid } from './NewsGrid';
import { useTheme } from '../../../basic/ThemeProvider';
import ThemeSwitcher from '../../../basic/ThemeSwitcher';
import { useNewNG } from '../context/NewNGContext';
import { newNGApi } from '../services/newNGApi';
import styles from '../NewNG.module.css';

// Video Info Section Component - Now imported from separate file

// News Sections Component - Now imported from separate files

// Helper function to select default job
const selectDefaultJob = (jobs: any[], initialJobId?: string): string | null => {
  // If no jobs available, return null
  if (!jobs || jobs.length === 0) {
    return null;
  }

  // Filter for completed jobs with videos (composition stage complete)
  const completeJobs = jobs.filter(job =>
    job.overall_status === 'completed' &&
    job.stages?.composition?.completed === true
  );

  // If initialJobId is provided, verify it's complete before using it
  if (initialJobId) {
    const initialJob = completeJobs.find(job => job.job_id === initialJobId);
    if (initialJob) {
      // Initial job exists and is complete, use it
      return initialJobId;
    }
    // Initial job is incomplete or not found, fall through to default logic
    console.warn(`Initial job ${initialJobId} is incomplete or not found, selecting newest complete job instead`);
  }

  // Sort by creation date (newest first) and get the latest
  const sortedJobs = completeJobs
    .filter(job => job.created_at)
    .sort((a, b) => {
      const dateA = new Date(a.created_at || '').getTime();
      const dateB = new Date(b.created_at || '').getTime();
      return dateB - dateA;
    });

  // Return the newest completed job ID, or null if none available
  return sortedJobs.length > 0 ? sortedJobs[0].job_id : null;
};

// Header Component
const Header: React.FC<{
  networkName: string;
  isDark: boolean;
  toggleTheme: () => void;
}> = ({ networkName, isDark, toggleTheme }) => (
  <header className={styles['newng-header']}>
    <div className={styles['newng-header-left']}>
      <button
        className={styles['newng-vmonad-logo-button']}
        onClick={() => window.location.href = '/game/'}
        title="Return to main page"
      >
        <img
          src="/news/demo/public/assets/logo.png"
          alt="vMonad Logo"
          className={styles['newng-vmonad-logo']}
        />
      </button>
      <div className={styles['newng-watch-live']}>
        <div className={styles['newng-live-dot']}></div>
        Watch Live
      </div>
      <div className={styles['newng-logo']}>
        <div className={styles['newng-logo-squares']}>
          {networkName.split('').map((letter, index) => (
            <div key={index} className={styles['newng-logo-square']}>{letter}</div>
          ))}
        </div>
      </div>
    </div>
    <div className={styles['newng-header-right']}>
      <button
        className={styles['newng-create-episode-button']}
        onClick={() => window.location.href = '/game/news-editor'}
        title="Create New Episode"
      >
        <span className={styles['newng-create-icon']}>+</span>
        Create Episode
      </button>
      <ThemeSwitcher isDark={isDark} onToggle={toggleTheme} />
      <button className={styles['newng-subscribe-button']}>Subscribe</button>
      <button type="button" className={styles['newng-signin']}>Sign In</button>
    </div>
  </header>
);

// Navigation Component
const Navigation: React.FC = () => {
  const navLinks = [
    'Home', 'News', 'Sport', 'Business', 'Innovation', 'Culture',
    'Arts', 'Travel', 'Earth', 'Audio', 'Video', 'Live'
  ];

  return (
    <nav className={styles['newng-navigation']}>
      <ul className={styles['newng-nav-links']}>
        {navLinks.map((link, index) => (
          <li key={index}>
            <button
              type="button"
              className={`${styles['newng-nav-link']} ${link === 'Home' ? styles.active : ''}`}
            >
              {link}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// Main NewNG Page Component
export const NewNGPage: React.FC<NewNGPageProps> = ({
  initialJobId,
  networkName = 'FEI' // Default to FEI if not provided
}) => {
  const { isDark, toggleTheme } = useTheme();
  const { state, dispatch } = useNewNG();
  const timelinedVideoRef = useRef<HTMLDivElement>(null);

  // Function to scroll to video section and take full viewport
  const scrollToVideoSection = useCallback(() => {
    if (timelinedVideoRef.current) {
      timelinedVideoRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      console.log('🎯 Scrolled to video section');
    }
  }, []);

  // Initialize data and load latest job
  const initializeData = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });



      // 1. Fetch all available jobs
      const jobs = await newNGApi.getJobs();

      // Store all jobs in state
      dispatch({ type: 'SET_JOBS', payload: jobs });

      // 2. Select default job using extracted function
      const targetJobId = selectDefaultJob(jobs, initialJobId);

      // 3. Set the current job if we have a target job ID
      if (targetJobId) {
        const targetJob = jobs.find(job => job.job_id === targetJobId);
        if (targetJob) {
          dispatch({ type: 'SET_CURRENT_JOB', payload: targetJob });
        } else {
          console.warn('Target job not found:', targetJobId);
        }
      }

    } catch (error) {
      console.error('Failed to initialize NewNG data:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load news data' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [initialJobId, dispatch]);

  useEffect(() => {
    initializeData();
  }, [initialJobId, initializeData]); // Re-run if initialJobId changes

  // Function to change the current job dynamically
  const changeJob = async (jobId: string) => {
    try {


      const targetJob = state.jobs.find(job => job.job_id === jobId);
      if (targetJob) {
        dispatch({ type: 'SET_CURRENT_JOB', payload: targetJob });

      } else {
        console.warn('Job not found in current jobs list:', jobId);
        // Try to fetch the specific job from the API
        const jobs = await newNGApi.getJobs();
        const freshJob = jobs.find(job => job.job_id === jobId);
        if (freshJob) {
          dispatch({ type: 'SET_CURRENT_JOB', payload: freshJob });

        } else {
          console.error('Job not found:', jobId);
        }
      }
    } catch (error) {
      console.error('Failed to change job:', error);
    }
  };

  return (
    <div className={styles['newng-container']}>
      <Header
        networkName={networkName}
        isDark={isDark}
        toggleTheme={toggleTheme}
      />
      <Navigation />

      <main className={styles['newng-main-content']}>
        <div ref={timelinedVideoRef}>
          <TimeLinedVideo
            jobId={state.currentJob?.job_id || ''}
            networkName={networkName}
            onJobChange={changeJob}
          />
        </div>
        <VideoInfoSection currentJob={state.currentJob} onVideoFocus={scrollToVideoSection} />

        {/* Top News Section - Separate Container */}
        <section className={styles['newng-news-section-container']}>
          <TopNews onVideoFocus={scrollToVideoSection} />
        </section>

        {/* More News Section - Separate Container */}
        <section className={styles['newng-news-section-container']}>
          <NewsGrid onVideoFocus={scrollToVideoSection} />
        </section>
      </main>

      <footer className={styles['newng-footer']}>
        <div className={styles['newng-footer-content']}>
          <div className={styles['newng-footer-disclaimer']}>
            <strong>Disclaimer:</strong><br />
            This website features content generated by AI. While we aim for accuracy, the information may not always be up-to-date or fully accurate. AI-generated articles are not reviewed by human editors and may reflect algorithmic biases. Always verify information independently.<br /><br />
            This content is not intended as professional advice. We are not liable for any actions taken based on the information here.<br /><br />
            For inquiries, contact us at {networkName} news @X
          </div>
        </div>
      </footer>
    </div>
  );
};